/**
 * Auto-Save System - State comparison based persistence
 *
 * Automatically saves changes by comparing UI state (_workflowsAtom, _phasesAtom)
 * with DB state (_workflowsInDBAtom, _phasesInDBAtom).
 *
 * Detects:
 * - CREATE: Phases with temp IDs (temp-xxx)
 * - UPDATE: Entities with different data (ignoring metadata)
 * - DELETE: Phases in DB but not in UI
 *
 * After successful API calls, synchronizes metadata (historyVersion, updatedAt)
 * in both UI and DB state atoms.
 */

import { atom, useAtom, type Getter, type Setter } from "jotai";
import * as React from "react";
import { _workflowsAtom, _workflowsInDBAtom } from "../core/workflows";
import { _phasesAtom, _phasesInDBAtom } from "../core/phases";
import { currentWorkflowIdAtom } from "../core/workflows";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import type { AtomStatus } from "@/frontend/types/atoms";
import {
  WorkflowSchema,
  PhaseSchema,
  UpdatePhaseBodySchema,
  CreatePhaseBodySchema,
  type WorkflowListItem,
  type Phase,
} from "@/isomorphic/schemas";
import { putValidated, postValidated, deleteValidated } from "@/frontend/lib/validation";
import { needsCreation } from "@/frontend/lib/utils/temp-id";
import {
  updatePhaseIdByPhaseKeyInHistoryAtom,
  updateWorkflowMetadataInHistoryAtom,
  updatePhaseMetadataInHistoryAtom,
} from "../actions/history-actions";

// ============================================================================
// ATOMS
// ============================================================================

function normalizeCallDataItem(callData: unknown): unknown {
  if (!callData || typeof callData !== "object") return callData;
  const raw = callData as Record<string, unknown>;
  const type = raw.type;
  const next: Record<string, unknown> = { ...raw };
  if ("conditions" in next && next.conditions && !Array.isArray(next.conditions)) {
    next.conditions = [];
  }
  if (type === "calculated_data" && typeof next.formula !== "string") {
    next.formula = "";
  }
  if (type === "transmitted_data") {
    if (typeof next.content !== "string") next.content = "";
    if (typeof next.beforeNextDataConstraint !== "boolean") next.beforeNextDataConstraint = false;
    if (typeof next.afterPreviousDataConstraint !== "boolean") next.afterPreviousDataConstraint = false;
    if (typeof next.frequency !== "string") next.frequency = "once";
  }
  return next;
}

function normalizePhaseForApi(phase: Phase): Phase {
  const callDatas = Array.isArray(phase.callDatas) ? phase.callDatas : [];
  const normalizedCallDatas = callDatas.map((item) => normalizeCallDataItem(item)) as Phase["callDatas"];
  return { ...phase, callDatas: normalizedCallDatas };
}

/**
 * Pause auto-save (e.g., when a modal is open)
 */
export const pauseAutoSaveAtom = atom(false);

/**
 * Save status atom (internal)
 */
const _saveStatusAtom = atom<AtomStatus>("init");

/**
 * Is saving before navigation (shows overlay during save)
 */
export const isSavingBeforeNavigationAtom = atom(false);

/**
 * Save status atom (for UI) - maps AtomStatus to UI-friendly status
 */
export const saveStatusAtom = atom((get) => {
  const status = get(_saveStatusAtom);
  // Map "init" to "idle" for UI compatibility
  if (status === "init") return "idle" as const;
  if (status === "loading") return "saving" as const;
  if (status === "success") return "saved" as const;
  if (status === "error") return "error" as const;
  return "idle" as const;
});

/**
 * Save error atom
 */
export const saveErrorAtom = atom<string | null>(null);

/**
 * Last save timestamp
 */
export const lastSaveTimestampAtom = atom<number | null>(null);

/**
 * Has unsaved changes - compares UI state with DB state
 */
export const hasUnsavedChangesAtom = atom((get) => {
  const workflowId = get(currentWorkflowIdAtom);
  if (!workflowId) return false;

  const uiWorkflows = get(_workflowsAtom);
  const dbWorkflows = get(_workflowsInDBAtom);
  const uiPhases = get(_phasesAtom).filter((p) => p.workflowId === workflowId);
  const dbPhases = get(_phasesInDBAtom).filter((p) => p.workflowId === workflowId);

  // Check workflow changes
  const uiWorkflow = uiWorkflows.find((w) => w.id === workflowId);
  const dbWorkflow = dbWorkflows.find((w) => w.id === workflowId);

  if (uiWorkflow && dbWorkflow && !workflowsAreEqual(uiWorkflow, dbWorkflow)) {
    return true;
  }

  // Check phase changes
  const phasesToCreate = uiPhases.filter((p) => needsCreation(p.id));
  if (phasesToCreate.length > 0) return true;

  const phasesToUpdate = uiPhases.filter((uiPhase) => {
    if (needsCreation(uiPhase.id)) return false;
    const dbPhase = dbPhases.find((p) => p.id === uiPhase.id);
    return dbPhase && !phasesAreEqual(uiPhase, dbPhase);
  });
  if (phasesToUpdate.length > 0) return true;

  const phasesToDelete = dbPhases.filter((dbPhase) => !uiPhases.some((p) => p.id === dbPhase.id));
  if (phasesToDelete.length > 0) return true;

  return false;
});

// ============================================================================
// STATE COMPARISON HELPERS
// ============================================================================

/**
 * Compare workflows ignoring metadata fields
 * Exported for use in history comparison
 */
export function workflowsAreEqual(a: WorkflowListItem, b: WorkflowListItem): boolean {
  return a.name === b.name && a.description === b.description && a.agentId === b.agentId;
}

/**
 * Compare phases ignoring metadata fields
 * Exported for use in history comparison
 */
export function phasesAreEqual(a: Phase, b: Phase): boolean {
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.workflowId === b.workflowId &&
    a.phaseKey === b.phaseKey &&
    a.orderIndex === b.orderIndex &&
    a.knowledgeBaseId === b.knowledgeBaseId &&
    JSON.stringify(a.callsConfiguration) === JSON.stringify(b.callsConfiguration) &&
    JSON.stringify(a.callDatas) === JSON.stringify(b.callDatas) &&
    JSON.stringify(a.advancementConditions) === JSON.stringify(b.advancementConditions) &&
    JSON.stringify(a.alerts) === JSON.stringify(b.alerts)
  );
}

// ============================================================================
// AUTO-SAVE ACTION ATOM
// ============================================================================

/**
 * Auto-save atom - Compares UI state with DB state and persists changes
 *
 * This atom should be called by the useAutoSave hook after debounce.
 * It detects CREATE, UPDATE, DELETE operations by comparing states.
 */
export const autoSaveAtom = atom(null, async (get, set) => {
  const workflowId = get(currentWorkflowIdAtom);

  if (!workflowId) {
    console.log("[AutoSave] No current workflow");
    return;
  }

  // Refresh JWT before saving to avoid expired token errors
  const { createClient } = await import("@/backend/lib/supabase/client");
  const supabase = createClient();
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.access_token) {
    console.error("[AutoSave] Failed to get valid session:", sessionError);
    set(saveErrorAtom, "Session expired. Please refresh the page.");
    return;
  }

  const jwt = session.access_token;

  const uiWorkflows = get(_workflowsAtom);
  const dbWorkflows = get(_workflowsInDBAtom);
  const uiPhases = get(_phasesAtom).filter((p) => p.workflowId === workflowId);
  const dbPhases = get(_phasesInDBAtom).filter((p) => p.workflowId === workflowId);

  set(_saveStatusAtom, "loading");
  set(saveErrorAtom, null);

  try {
    console.log("[AutoSave] Checking workflow and phases state for auto-save:", workflowId);
    console.log("[AutoSave] UI phases:", uiPhases.map((p) => p.id).join(", "));
    console.log("[AutoSave] DB phases:", dbPhases.map((p) => p.id).join(", "));

    // 1. Save workflow updates
    const uiWorkflow = uiWorkflows.find((w) => w.id === workflowId);
    const dbWorkflow = dbWorkflows.find((w) => w.id === workflowId);

    if (uiWorkflow && dbWorkflow && !workflowsAreEqual(uiWorkflow, dbWorkflow)) {
      console.log("[AutoSave] Workflow changed, updating in DB...");
      await saveWorkflowUpdate(uiWorkflow, jwt, get, set);
    }

    // 2. Detect phase operations
    // CREATE: temp ID OR UUID not in DB (re-creation after undo)
    const phasesToCreate = uiPhases.filter((p) => {
      if (needsCreation(p.id)) return true; // Temp ID
      const existsInDB = dbPhases.some((dbPhase) => dbPhase.id === p.id);
      if (!existsInDB) {
        console.log("[AutoSave] Phase to re-create:", p.id, "not in DB");
      }
      return !existsInDB; // UUID but not in DB = re-creation
    });

    // UPDATE: UUID in DB with different data
    const phasesToUpdate = uiPhases.filter((uiPhase) => {
      if (needsCreation(uiPhase.id)) return false; // Skip temp IDs
      const dbPhase = dbPhases.find((p) => p.id === uiPhase.id);
      if (!dbPhase) return false; // Not in DB = will be created
      return !phasesAreEqual(uiPhase, dbPhase);
    });

    // DELETE: in DB but not in UI
    const phasesToDelete = dbPhases.filter((dbPhase) => !uiPhases.some((p) => p.id === dbPhase.id));

    console.log("[AutoSave] Phase operations:", {
      create: phasesToCreate.length,
      update: phasesToUpdate.length,
      delete: phasesToDelete.length,
    });

    // 3. Execute phase operations
    for (const phase of phasesToCreate) {
      await createPhase(phase, jwt, get, set);
    }

    for (const phase of phasesToUpdate) {
      await updatePhase(phase, jwt, get, set);
    }

    for (const phase of phasesToDelete) {
      await deletePhase(phase.id, jwt, get, set);
    }

    set(_saveStatusAtom, "success");
    set(lastSaveTimestampAtom, Date.now());

    console.log("[AutoSave] Auto-save completed successfully");

    // Reset status after 3 seconds
    setTimeout(() => {
      set(_saveStatusAtom, "init");
    }, 3000);
  } catch (error) {
    console.error("[AutoSave] Auto-save failed:", error);
    set(_saveStatusAtom, "error");
    set(saveErrorAtom, error instanceof Error ? error.message : "Unknown error");

    // Reset error after 5 seconds
    setTimeout(() => {
      set(_saveStatusAtom, "init");
      set(saveErrorAtom, null);
    }, 5000);
  }
});

// ============================================================================
// OPERATION FUNCTIONS
// ============================================================================

/**
 * Update workflow in backend
 */
async function saveWorkflowUpdate(workflow: WorkflowListItem, jwt: string, get: Getter, set: Setter): Promise<void> {
  console.log("[AutoSave] Updating workflow:", workflow.id);

  const result = await putValidated(
    WorkflowSchema,
    API_ROUTES.workflows.update(workflow.id),
    {
      historyVersion: workflow.historyVersion,
      name: workflow.name,
      description: workflow.description,
    },
    { jwt, context: `Auto-save Workflow ${workflow.id}` },
  );

  if (result.success) {
    console.log("[AutoSave] Workflow updated:", workflow.id);

    // Synchronize metadata in UI state
    const uiWorkflows = get(_workflowsAtom);
    set(
      _workflowsAtom,
      uiWorkflows.map((w) =>
        w.id === workflow.id
          ? { ...w, historyVersion: result.data.historyVersion, updatedAt: result.data.updatedAt }
          : w,
      ),
    );

    // Synchronize FULL state in DB state (not just metadata!)
    const dbWorkflows = get(_workflowsInDBAtom);
    set(
      _workflowsInDBAtom,
      dbWorkflows.map((w) =>
        w.id === workflow.id
          ? { ...result.data } // Use full server response
          : w,
      ),
    );

    // Update metadata in ALL history states
    set(updateWorkflowMetadataInHistoryAtom, workflow.id, {
      historyVersion: result.data.historyVersion,
      updatedAt: result.data.updatedAt,
    });

    console.log("[AutoSave] DB state and history synchronized for workflow:", workflow.id);
  } else {
    console.error("[AutoSave] Error updating workflow:", workflow.id, result.error);
    throw new Error(result.error);
  }
}

/**
 * Create phase in backend
 */
async function createPhase(phase: Phase, jwt: string, get: Getter, set: Setter): Promise<void> {
  console.log("[AutoSave] Creating phase:", phase.id);

  const normalizedPhase = normalizePhaseForApi(phase);
  const createPayload = {
    workflowId: normalizedPhase.workflowId,
    phaseKey: normalizedPhase.phaseKey,
    orderIndex: normalizedPhase.orderIndex,
    name: normalizedPhase.name,
    description: normalizedPhase.description || undefined,
    knowledgeBaseId: normalizedPhase.knowledgeBaseId,
    callsConfiguration: normalizedPhase.callsConfiguration,
    callDatas: normalizedPhase.callDatas ?? [],
    advancementConditions: normalizedPhase.advancementConditions || [],
    alerts: normalizedPhase.alerts || [],
  };

  const parseResult = CreatePhaseBodySchema.safeParse(createPayload);

  if (!parseResult.success) {
    console.error("[AutoSave] Invalid create phase payload:", phase.id, parseResult.error.format());
    throw new Error(`Invalid create phase data: ${parseResult.error.message}`);
  }

  const result = await postValidated(PhaseSchema, API_ROUTES.phases.create, createPayload, {
    jwt,
    context: `Create Phase ${phase.id}`,
  });

  if (result.success) {
    console.log("[AutoSave] Phase created:", result.data.id, "(was", phase.id, ")");

    const tempId = phase.id;
    const uiPhases = get(_phasesAtom);

    // Update both UI and DB state atomically (same data, no intermediate state)
    const updatedPhases = uiPhases.map((p) => (p.id === tempId ? result.data : p));

    set(_phasesAtom, updatedPhases);
    set(_phasesInDBAtom, updatedPhases); // Same data in both atoms

    // Update history: replace temp ID with UUID (search by phaseKey)
    set(updatePhaseIdByPhaseKeyInHistoryAtom, phase.workflowId, phase.phaseKey, result.data.id);

    console.log("[AutoSave] Phase ID synchronized:", tempId, "→", result.data.id);
  } else {
    console.error("[AutoSave] Error creating phase:", phase.id, result.error);
    throw new Error(result.error);
  }
}

/**
 * Update phase in backend
 */
async function updatePhase(phase: Phase, jwt: string, get: Getter, set: Setter): Promise<void> {
  console.log("[AutoSave] Updating phase:", phase.id);

  const normalizedPhase = normalizePhaseForApi(phase);
  const rawPayload = {
    historyVersion: normalizedPhase.historyVersion,
    name: normalizedPhase.name,
    description: normalizedPhase.description,
    knowledgeBaseId: normalizedPhase.knowledgeBaseId,
    callsConfiguration: normalizedPhase.callsConfiguration,
    callDatas: normalizedPhase.callDatas,
    advancementConditions: normalizedPhase.advancementConditions,
    alerts: normalizedPhase.alerts,
  };

  const parseResult = UpdatePhaseBodySchema.safeParse(rawPayload);

  if (!parseResult.success) {
    console.error("[AutoSave] Invalid phase data:", phase.id, parseResult.error.format());
    throw new Error(`Invalid phase data: ${parseResult.error.message}`);
  }

  const result = await putValidated(PhaseSchema, API_ROUTES.phases.update(phase.id), parseResult.data, {
    jwt,
    context: `Auto-save Phase ${phase.id}`,
  });

  if (result.success) {
    console.log("[AutoSave] Phase updated:", phase.id);

    // Synchronize metadata in UI state
    const uiPhases = get(_phasesAtom);
    set(
      _phasesAtom,
      uiPhases.map((p) =>
        p.id === phase.id ? { ...p, historyVersion: result.data.historyVersion, updatedAt: result.data.updatedAt } : p,
      ),
    );

    // Synchronize FULL state in DB state (not just metadata!)
    const dbPhases = get(_phasesInDBAtom);
    set(
      _phasesInDBAtom,
      dbPhases.map((p) =>
        p.id === phase.id
          ? { ...result.data } // Use full server response
          : p,
      ),
    );

    // Update metadata in ALL history states
    set(updatePhaseMetadataInHistoryAtom, phase.workflowId, phase.id, {
      historyVersion: result.data.historyVersion,
      updatedAt: result.data.updatedAt,
    });

    console.log("[AutoSave] DB state and history synchronized for phase:", phase.id);
  } else {
    console.error("[AutoSave] Error updating phase:", phase.id, result.error);
    throw new Error(result.error);
  }
}

/**
 * Delete phase from backend
 */
async function deletePhase(phaseId: string, jwt: string, get: Getter, set: Setter): Promise<void> {
  console.log("[AutoSave] Deleting phase:", phaseId);

  const result = await deleteValidated(API_ROUTES.phases.delete(phaseId), {
    jwt,
    context: `Delete Phase ${phaseId}`,
  });

  if (result.success) {
    console.log("[AutoSave] Phase deleted:", phaseId);

    // Remove from DB state
    const dbPhases = get(_phasesInDBAtom);
    set(
      _phasesInDBAtom,
      dbPhases.filter((p) => p.id !== phaseId),
    );
  } else {
    // If 404, phase already deleted - just update DB state
    if (result.error.includes("not found") || result.error.includes("Invalid phase ID")) {
      console.warn("[AutoSave] Phase already deleted:", phaseId);
      const dbPhases = get(_phasesInDBAtom);
      set(
        _phasesInDBAtom,
        dbPhases.filter((p) => p.id !== phaseId),
      );
    } else {
      console.error("[AutoSave] Error deleting phase:", phaseId, result.error);
      throw new Error(result.error);
    }
  }
}

// ============================================================================
// REACT HOOK
// ============================================================================

/**
 * useAutoSave hook - Triggers auto-save after debounce
 *
 * Watches for changes in UI state and triggers auto-save after debounce period.
 *
 * Usage:
 * ```tsx
 * const MyComponent = () => {
 *   useAutoSave()
 *   // ...
 * }
 * ```
 */
export function useAutoSave(debounceMs: number = 3000) {
  const [uiWorkflows] = useAtom(_workflowsAtom);
  const [uiPhases] = useAtom(_phasesAtom);
  const [, autoSave] = useAtom(autoSaveAtom);
  const [saveStatus] = useAtom(saveStatusAtom);
  const [isPaused] = useAtom(pauseAutoSaveAtom);

  React.useEffect(() => {
    // Don't save if paused (e.g., modal is open)
    if (isPaused) {
      console.log("[AutoSave] Paused - modal is open");
      return;
    }

    // Don't save if already saving
    if (saveStatus === "saving") {
      return;
    }

    // Debounce: wait for user to stop typing
    const timeoutId = setTimeout(() => {
      autoSave();
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [uiWorkflows, uiPhases, autoSave, saveStatus, debounceMs, isPaused]);
}
